import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

const TermsOfService = () => {
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center text-gray-600 hover:text-blue-600"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Back
              </button>
            </div>
            <div className="flex-shrink-0">
              <h1 className="text-2xl font-bold text-blue-600">W3Skills</h1>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <DocumentTextIcon className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Terms of Service
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Please read these terms carefully before using our platform. By using W3Skills, you agree to these terms.
            </p>
            <p className="text-sm text-gray-500 mt-4">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing and using W3Skills ("the Platform"), you accept and agree to be bound by the terms 
              and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
            </p>

            <h2>2. Description of Service</h2>
            <p>
              W3Skills is an educational platform designed to help students prepare for the MH-CET examination. 
              Our services include:
            </p>
            <ul>
              <li>Interactive learning modules and practice questions</li>
              <li>Mock tests and performance analytics</li>
              <li>AI-powered study recommendations</li>
              <li>College prediction tools</li>
              <li>Progress tracking and gamification features</li>
            </ul>

            <h2>3. User Accounts</h2>
            <p>When you create an account with us, you must provide accurate and complete information. You are responsible for:</p>
            <ul>
              <li>Maintaining the security of your account and password</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized use</li>
              <li>Ensuring your account information is up to date</li>
            </ul>

            <h2>4. Subscription and Payment</h2>
            <p>
              We offer both free and premium subscription plans. By subscribing to a paid plan, you agree to:
            </p>
            <ul>
              <li>Pay all fees associated with your chosen plan</li>
              <li>Provide accurate billing information</li>
              <li>Authorize us to charge your payment method</li>
              <li>Understand that fees are non-refundable except as required by law</li>
            </ul>

            <h2>5. Acceptable Use</h2>
            <p>You agree not to use the Platform to:</p>
            <ul>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on the rights of others</li>
              <li>Share or distribute content that is harmful, offensive, or inappropriate</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Use automated tools to access the platform</li>
              <li>Share your account credentials with others</li>
            </ul>

            <h2>6. Intellectual Property</h2>
            <p>
              The Platform and its original content, features, and functionality are owned by W3Skills and are 
              protected by international copyright, trademark, patent, trade secret, and other intellectual 
              property laws.
            </p>

            <h2>7. User Content</h2>
            <p>
              You retain ownership of any content you submit to the Platform. By submitting content, you grant 
              us a non-exclusive, worldwide, royalty-free license to use, modify, and display your content 
              in connection with the Platform.
            </p>

            <h2>8. Privacy</h2>
            <p>
              Your privacy is important to us. Please review our Privacy Policy, which also governs your use 
              of the Platform, to understand our practices.
            </p>

            <h2>9. Disclaimers</h2>
            <p>
              The Platform is provided "as is" without warranties of any kind. We do not guarantee:
            </p>
            <ul>
              <li>That the Platform will be error-free or uninterrupted</li>
              <li>The accuracy of predictions or recommendations</li>
              <li>Specific results from using our services</li>
              <li>Compatibility with all devices or browsers</li>
            </ul>

            <h2>10. Limitation of Liability</h2>
            <p>
              In no event shall W3Skills be liable for any indirect, incidental, special, consequential, 
              or punitive damages, including without limitation, loss of profits, data, use, goodwill, 
              or other intangible losses.
            </p>

            <h2>11. Termination</h2>
            <p>
              We may terminate or suspend your account and access to the Platform immediately, without prior 
              notice, for any reason, including breach of these Terms of Service.
            </p>

            <h2>12. Changes to Terms</h2>
            <p>
              We reserve the right to modify these terms at any time. We will notify users of any material 
              changes by posting the new terms on the Platform and updating the "Last updated" date.
            </p>

            <h2>13. Governing Law</h2>
            <p>
              These terms shall be governed by and construed in accordance with the laws of India, without 
              regard to its conflict of law provisions.
            </p>

            <h2>14. Contact Information</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <p>
              <strong>Email:</strong> <a href="mailto:w3skills.devstudio@gmail.com" className="text-blue-600 hover:underline">w3skills.devstudio@gmail.com</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
